import { getPriceConfig } from '@/config/deliveryPrices';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface DeliveryCalculationResult {
  distanceKm: number;
  durationMin: number;
  fee: number;
  basePrice: number;
  pricePerKm: number;
}

export interface RouteDirectionsResult {
  points: LatLng[];
  distanceKm: number;
  durationMin: number;
  steps: RouteDirectionStep[];
}

export interface RouteDirectionStep {
  instruction: string;
  distanceMeters: number;
  durationSeconds: number;
  startIndex: number;
  endIndex: number;
}

// You must set your OpenRouteService API key in the .env file as VITE_ORS_API_KEY
const ORS_API_KEY = import.meta.env.VITE_ORS_API_KEY;

const ROUTE_DEBUG = import.meta.env.DEV;

function logRouteDebug(stage: string, payload: Record<string, unknown>) {
  if (!ROUTE_DEBUG) return;
  console.debug('[RouteDebug]', stage, payload);
  if (typeof window !== 'undefined') {
    (window as any).__routeDebugLast = {
      stage,
      payload,
      at: new Date().toISOString(),
    };
  }
}


export async function calculateDeliveryFee(
  origin: LatLng,
  destination: LatLng,
  transportType: string = 'auto',
  serviceType: string = 'persona',
  subtotal?: number | null
): Promise<DeliveryCalculationResult> {
  const buildFeeFromDistance = (distanceKm: number, durationMin: number): DeliveryCalculationResult => {
    const priceConfig = getPriceConfig(transportType, serviceType);
    let fee = Math.round(priceConfig.base + distanceKm * priceConfig.perKm);
    if (serviceType === 'paquete' && subtotal && subtotal > 0) {
      const extra = Math.round(subtotal * 0.01);
      fee = fee + extra;
    }

    return {
      distanceKm,
      durationMin,
      fee,
      basePrice: priceConfig.base,
      pricePerKm: priceConfig.perKm,
    };
  };

  if (!ORS_API_KEY) {
    console.warn('[Delivery] ORS API key missing, using fallback route provider');
    const osrmRoute = await getOsrmRouteDirections(origin, destination);
    if (osrmRoute) return buildFeeFromDistance(osrmRoute.distanceKm, osrmRoute.durationMin);
    const fallbackRoute = createFallbackRouteDirections(origin, destination);
    return buildFeeFromDistance(fallbackRoute.distanceKm, fallbackRoute.durationMin);
  }
  const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${ORS_API_KEY}`;
  const body = {
    coordinates: [
      [origin.lng, origin.lat],
      [destination.lng, destination.lat]
    ]
  };
  if (process.env.NODE_ENV === 'development') {
    console.debug('[Delivery] ORS API Key (hidden in prod):', '***');
    console.debug('[Delivery] Request URL:', url);
    console.debug('[Delivery] Request Body:', body);
  }
  try {
    logRouteDebug('ors.request', { url, body });
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Delivery] Raw response:', res);
    }
    const text = await res.text();
    logRouteDebug('ors.response', { status: res.status, ok: res.ok, textLength: text.length });
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Delivery] Response text:', text);
    }
    let data;
    try {
      data = JSON.parse(text);
    } catch (jsonErr) {
      console.error('[Delivery] Error parsing JSON:', jsonErr);
      throw new Error('Error parsing ORS response');
    }
    console.debug('[Delivery] Parsed response:', data);
    // Fix: ORS response for /v2/directions/driving-car has summary in data.routes[0].summary
    const summary = data.routes?.[0]?.summary;
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Delivery] Route summary:', summary);
    }
    if (!summary) throw new Error('No route summary found');
    const distanceKm = summary.distance / 1000;
    const durationMin = summary.duration / 60;
    
    const result = buildFeeFromDistance(distanceKm, durationMin);
    console.debug('[Delivery] Calculated fee:', result.fee, 'Distance (km):', distanceKm, 'Duration (min):', durationMin);
    return result;
  } catch (err) {
    console.error('[Delivery] ORS error, trying fallback:', err);
    const osrmRoute = await getOsrmRouteDirections(origin, destination);
    if (osrmRoute) return buildFeeFromDistance(osrmRoute.distanceKm, osrmRoute.durationMin);
    const fallbackRoute = createFallbackRouteDirections(origin, destination);
    return buildFeeFromDistance(fallbackRoute.distanceKm, fallbackRoute.durationMin);
  }
}


function createFallbackRouteDirections(origin: LatLng, destination: LatLng): RouteDirectionsResult {
  logRouteDebug('fallback.start', { origin, destination });
  const roughDistanceKm = (() => {
    const R = 6371;
    const dLat = ((destination.lat - origin.lat) * Math.PI) / 180;
    const dLng = ((destination.lng - origin.lng) * Math.PI) / 180;
    const p1 = (origin.lat * Math.PI) / 180;
    const p2 = (destination.lat * Math.PI) / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  })();

  const distanceKm = Number.isFinite(roughDistanceKm) ? roughDistanceKm : 0;
  const durationMin = Math.max(1, Math.round((distanceKm / 30) * 60));

  const result = {
    points: [origin, destination],
    distanceKm,
    durationMin,
    steps: [
      {
        instruction: 'Dirigite al punto de retiro siguiendo la ruta del mapa',
        distanceMeters: Math.max(100, Math.round(distanceKm * 1000)),
        durationSeconds: Math.max(60, Math.round(durationMin * 60)),
        startIndex: 0,
        endIndex: 1,
      },
    ],
  };

  logRouteDebug('fallback.result', {
    pointsCount: result.points.length,
    stepsCount: result.steps.length,
    distanceKm: result.distanceKm,
    durationMin: result.durationMin,
  });

  return result;
}



function buildOsrmInstructionEs(step: any): string {
  const maneuver = step?.maneuver || {};
  const type = String(maneuver.type || '').toLowerCase();
  const modifier = String(maneuver.modifier || '').toLowerCase();
  const name = step?.name ? ` por ${step.name}` : '';

  if (type === 'depart') return `Comenza el recorrido${name}`;
  if (type === 'arrive') return 'Llegaste al destino';
  if (type === 'roundabout' || type === 'rotary') return `En la rotonda, toma la salida indicada${name}`;
  if (type === 'merge') return `Incorpora${name}`;
  if (type === 'fork') return `Mantene la bifurcacion${name}`;
  if (type === 'end of road') return `Al final de la calle, gira${name}`;
  if (type === 'new name') return `Continua por${name}`;
  if (type === 'continue') return `Segui derecho${name}`;

  if (modifier === 'left' || modifier === 'slight left' || modifier === 'sharp left') {
    return `Gira a la izquierda${name}`;
  }
  if (modifier === 'right' || modifier === 'slight right' || modifier === 'sharp right') {
    return `Gira a la derecha${name}`;
  }
  if (modifier === 'straight') return `Segui derecho${name}`;
  if (modifier === 'uturn') return 'Realiza un giro en U';

  return `Segui por la calle actual${name}`;
}

async function getOsrmRouteDirections(origin: LatLng, destination: LatLng): Promise<RouteDirectionsResult | null> {
  try {
    logRouteDebug('osrm.request', { origin, destination });
    const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson&steps=true`;
    const osrmRes = await fetch(osrmUrl);
    logRouteDebug('osrm.response', { status: osrmRes.status, ok: osrmRes.ok });
    if (!osrmRes.ok) return null;

    const data = await osrmRes.json();
    const route = data?.routes?.[0];
    const coordinates = route?.geometry?.coordinates as Array<[number, number]> | undefined;

    if (!route || !Array.isArray(coordinates) || coordinates.length < 2) {
      logRouteDebug('osrm.invalid-route', { hasRoute: Boolean(route), coordinatesCount: Array.isArray(coordinates) ? coordinates.length : 0 });
      return null;
    }

    const points = coordinates.map(([lng, lat]) => ({ lat, lng }));
    const stepsRaw = (route?.legs || []).flatMap((leg: any) => Array.isArray(leg?.steps) ? leg.steps : []);

    const steps: RouteDirectionStep[] = stepsRaw.map((step: any) => ({
      instruction: buildOsrmInstructionEs(step),
      distanceMeters: Number(step?.distance || 0),
      durationSeconds: Number(step?.duration || 0),
      startIndex: 0,
      endIndex: Math.max(1, points.length - 1),
    }));

    const result = {
      points,
      distanceKm: Number(route.distance || 0) / 1000,
      durationMin: Number(route.duration || 0) / 60,
      steps: steps.length > 0
        ? steps
        : [{
            instruction: 'Segui la ruta vial sugerida en el mapa',
            distanceMeters: Math.max(100, Math.round(Number(route.distance || 0))),
            durationSeconds: Math.max(60, Math.round(Number(route.duration || 0))),
            startIndex: 0,
            endIndex: Math.max(1, points.length - 1),
          }],
    };

    logRouteDebug('osrm.result', {
      pointsCount: result.points.length,
      stepsCount: result.steps.length,
      distanceKm: result.distanceKm,
      durationMin: result.durationMin,
    });

    return result;
  } catch (err) {
    logRouteDebug('osrm.error', { error: err instanceof Error ? err.message : String(err) });
    return null;
  }
}

export async function getRouteDirections(
  origin: LatLng,
  destination: LatLng
): Promise<RouteDirectionsResult> {
  logRouteDebug('ors.entry', { origin, destination, hasOrsKey: Boolean(ORS_API_KEY) });
  if (!ORS_API_KEY) {
    console.warn('[Delivery] ORS API key missing, trying OSRM fallback');
    const osrmRoute = await getOsrmRouteDirections(origin, destination);
    if (osrmRoute) return osrmRoute;
    return createFallbackRouteDirections(origin, destination);
  }

  // Use ORS GeoJSON endpoint to guarantee decoded street geometry coordinates.
  const url = `https://api.openrouteservice.org/v2/directions/driving-car/geojson?api_key=${ORS_API_KEY}`;
  const body = {
    coordinates: [
      [origin.lng, origin.lat],
      [destination.lng, destination.lat],
    ],
    language: 'es',
    instructions: true,
  };

  try {
    logRouteDebug('ors.request', { url, body });
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const text = await res.text();
    logRouteDebug('ors.response', { status: res.status, ok: res.ok, textLength: text.length });
    let data: any;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error('Error parsing ORS route response');
    }

    const feature = Array.isArray(data?.features) ? data.features[0] : null;
    const summary = feature?.properties?.summary;
    const coordinates = feature?.geometry?.coordinates as Array<[number, number]> | undefined;
    const segments = Array.isArray(feature?.properties?.segments) ? feature.properties.segments : [];
    const rawSteps = segments.flatMap((segment: any) => (Array.isArray(segment?.steps) ? segment.steps : [])) as
      Array<{
        instruction?: string;
        distance?: number;
        duration?: number;
        way_points?: [number, number];
      }>;

    if (!summary || !Array.isArray(coordinates) || coordinates.length < 2) {
      logRouteDebug('ors.invalid-route', {
        hasSummary: Boolean(summary),
        coordinatesCount: Array.isArray(coordinates) ? coordinates.length : 0,
      });
      throw new Error('No valid street route returned by ORS geojson');
    }

    const points = coordinates.map(([lng, lat]) => ({ lat, lng }));

    const parsedSteps: RouteDirectionStep[] = rawSteps
      .map((step) => {
        const wayPoints = step.way_points;
        const startIndex = Array.isArray(wayPoints) ? wayPoints[0] : 0;
        const endIndex = Array.isArray(wayPoints) ? wayPoints[1] : 0;
        return {
          instruction: step.instruction || 'Sigue recto',
          distanceMeters: Number(step.distance || 0),
          durationSeconds: Number(step.duration || 0),
          startIndex: Number.isFinite(startIndex) ? startIndex : 0,
          endIndex: Number.isFinite(endIndex) ? endIndex : 0,
        };
      })
      .filter((step) => step.instruction && step.endIndex >= step.startIndex);

    const steps = parsedSteps.length > 0
      ? parsedSteps
      : [
          {
            instruction: 'Segui la ruta vial sugerida en el mapa',
            distanceMeters: Math.max(100, Math.round(summary.distance || 0)),
            durationSeconds: Math.max(60, Math.round(summary.duration || 0)),
            startIndex: 0,
            endIndex: Math.max(1, points.length - 1),
          },
        ];

    const result = {
      points,
      distanceKm: Number(summary.distance || 0) / 1000,
      durationMin: Number(summary.duration || 0) / 60,
      steps,
    };

    logRouteDebug('ors.result', {
      pointsCount: result.points.length,
      stepsCount: result.steps.length,
      distanceKm: result.distanceKm,
      durationMin: result.durationMin,
      firstInstruction: result.steps[0]?.instruction || null,
    });

    return result;
  } catch (err) {
    logRouteDebug('ors.error', { error: err instanceof Error ? err.message : String(err) });
    console.warn('[Delivery] ORS geojson failed, trying OSRM fallback:', err);
    const osrmRoute = await getOsrmRouteDirections(origin, destination);
    if (osrmRoute) return osrmRoute;
    return createFallbackRouteDirections(origin, destination);
  }
}
